import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import ExecutiveDashboard from "@/pages/dashboard/ExecutiveDashboard";
import ExecutiveAlertSettings from "@/pages/dashboard/ExecutiveAlertSettings";
import ExecutiveTargets from "@/pages/dashboard/ExecutiveTargets";
import ExecutiveReports from "@/pages/dashboard/ExecutiveReports";
import ExecutiveAuditView from "@/pages/dashboard/ExecutiveAuditView";
import ExecutiveAlertAnalytics from "@/pages/dashboard/ExecutiveAlertAnalytics";
import ExecutiveDigest from "@/pages/dashboard/ExecutiveDigest";
import ExecutiveForecastHistory from "@/pages/dashboard/ExecutiveForecastHistory";
import ExecutiveThresholdChanges from "@/pages/dashboard/ExecutiveThresholdChanges";
import ExecutiveScenarios from "@/pages/dashboard/ExecutiveScenarios";
import ExecutiveScenarioDetail from "@/pages/dashboard/ExecutiveScenarioDetail";
import ExecutiveScenarioComparison from "@/pages/dashboard/ExecutiveScenarioComparison";
import UsersPage from "@/pages/UsersPage";
import AuditPage from "@/pages/AuditPage";
import SettingsPage from "@/pages/SettingsPage";
import DemoSettingsPage from "@/pages/DemoSettingsPage";
import ProfilePage from "@/pages/ProfilePage";
import NotFoundPage from "@/pages/NotFoundPage";
import ForbiddenPage from "@/pages/ForbiddenPage";
import AppLayout from "@/components/layout/AppLayout";
import MasterDataLayout, { MASTER_LIST } from "@/pages/masters/MasterDataLayout";
import MasterDataHub from "@/pages/masters/MasterDataHub";
import NumberingPage from "@/pages/masters/NumberingPage";
import { MasterPage } from "@/pages/masters/MasterPage";
import {
  TransactionListPage, PaymentPage, AgingPage, ApprovalInboxPage, PaymentCenterPage,
  SalesHub, PurchaseHub, FinanceHub, ExpensePage, TransferPage, DocumentsPage,
} from "@/pages/txn/TxnPages";
import {
  AccountingHub, TrialBalancePage, GeneralLedgerPage, JournalListPage,
  ReportsHub, IntegrityPage, AssetsPage,
} from "@/pages/acc/AccountingPages";
import { OpeningBalancePage } from "@/pages/acc/OpeningBalancePage";
import { TaxDashboard, TaxPPNHub, TaxFakturPage, TaxIntegrityPage, TaxProfilePage } from "@/pages/tax/TaxPages";
import { PPhHub, TaxPaymentPage, TaxCalendarPage, CompliancePage, PPhReconciliationPage } from "@/pages/tax/PPhPages";
import { SPTPage, ArsipPage, CoretaxCenterPage, FinalIntegrityPage } from "@/pages/tax/Tahap5CPages";
import { CoretaxIOPage, ExecutiveDashboardPage } from "@/pages/tax/Tahap5CGel3Pages";
import {
  InventoryHub, StockBalancePage, StockCardPage, MovementPage, TransferPage as InvTransferPage,
  AdjustmentPage, OpnamePage, BatchPage, OpeningBalancePage as InvOpeningBalancePage,
  CostingSettingsPage, IntegrityPage as InvIntegrityPage,
} from "@/pages/inventory/InventoryPages";
import {
  Integrity6BPage as InvIntegrity6BPage, TransferOrderPage as InvTransferOrderPage,
} from "@/pages/inventory/Inventory6BPages";
import {
  ManagementDashboard as Inv6CDash, ExpiryPage as Inv6CExpiry, ReorderPage as Inv6CReorder,
  AgingPage as Inv6CAging, MarginPage as Inv6CMargin, NotificationCenter as Inv6CNotif,
  Integrity6CPage as Inv6CIntegrity,
} from "@/pages/inventory/Inventory6CPages";
import {
  BankHub, BankAccountsPage, StatementImportPage, MatchingPage, BankTransferPage,
  BankChargesPage, ReconciliationPage, BankIntegrityPage,
} from "@/pages/banking/BankingPages";
import {
  FinancialHub, CashFlowPage, ARAgingPage, APAgingPage, CashPositionPage,
  ManagementDashboardPage, FinancialIntegrityPage, FinancialClosePage,
  FixedAssetRegisterPage, DepreciationBatchPage, AssetDisposalPage,
  BankAutoRulesPage,
} from "@/pages/financial/Tahap7BPages";
import { ExportsHub, ExportAuditLogPage, CompanyProfilePage } from "@/pages/financial/ExportsPages";
import {
  BudgetHub, BudgetListPage, BudgetEditPage, BudgetVariancePage,
  BudgetDashboardPage, ForecastPage, DepreciationMethodsPage, Exports7DHub,
} from "@/pages/financial/Tahap7DPages";
import { HRHub, EmployeeListPage, EmployeeEditPage, HRPolicyListPage } from "@/pages/hr/HRPages";
import {
  CalendarPage, AttendanceClockPage, LeavePage, OvertimePage,
  AttendanceHub, CorrectionListPage, Integrity8BPage,
} from "@/pages/hr/AttendancePages";
import {
  PayrollHub, PayrollDashboard, PayrollComponents, SalaryStructurePage,
  PayrollPeriodsPage, PayrollPeriodDetail, LoanPage, PaymentBatchPage,
  MyPayslipPage, PayrollIntegrityPage
} from "@/pages/hr/PayrollPages";
import {
  ClaimHub, ClaimListPage, ClaimApprovalPage, ClaimIntegrityPage,
  RecruitmentHub, MPRPage, VacancyPage, CandidatePage, OfferPage, RecruitmentIntegrityPage
} from "@/pages/hr/ClaimRecruitmentPages";
import {
  OnboardingHub, OnboardingListPage, OnboardingDetailPage,
  OnboardingTemplatePage, ProbationPage, OnboardingIntegrityPage,
  OffboardingHub, OffboardingListPage, OffboardingDetailPage,
  AssetAssignmentsPage, OffboardingIntegrityPage,
} from "@/pages/hr/OnboardingOffboardingPages";
import {
  HRDDashboardPage, AdminDashboardPage,
  ESSHomePage, ESSAttendancePage, ESSLeavePage, ESSOvertimePage,
  ESSClaimPage, ESSPayslipPage, ESSLoanPage, ESSDocumentsPage,
  ESSOnboardingTasksPage,
  MSSPage, ApprovalCenterPage, NotificationCenterPage,
  PasswordResetAdminPage, PasswordResetConfirmPage,
  DocumentCenterPage, ReportCenterPage,
} from "@/pages/hr/Tahap8HPages";

const TXN_CFG = {
  sales_order: { title: "Pesanan Penjualan", description: "Buat, ajukan, dan setujui pesanan penjualan.", label: "Pesanan Penjualan", addLabel: "Buat Pesanan" },
  sales_invoice: { title: "Invoice Penjualan", description: "Invoice tercatat pada piutang saat disetujui.", label: "Invoice Penjualan", addLabel: "Buat Invoice" },
  sales_credit_note: { title: "Nota Kredit", description: "Kurangi piutang atas retur/koreksi.", label: "Nota Kredit" },
  purchase_order: { title: "Pesanan Pembelian", description: "Buat dan setujui pesanan pembelian.", label: "Pesanan Pembelian", addLabel: "Buat PO" },
  supplier_bill: { title: "Tagihan Pemasok", description: "Tagihan tercatat pada hutang saat disetujui.", label: "Tagihan Pemasok", addLabel: "Catat Tagihan" },
  purchase_debit_note: { title: "Nota Debit", description: "Kurangi hutang atas retur/koreksi.", label: "Nota Debit" },
  customer_advance: { title: "Uang Muka Pelanggan", description: "Terima uang muka dari pelanggan.", label: "Uang Muka Pelanggan" },
  supplier_advance: { title: "Uang Muka Pemasok", description: "Bayar uang muka ke pemasok.", label: "Uang Muka Pemasok" },
  customer_receipt: { title: "Penerimaan Pelanggan", description: "Catat pembayaran dari pelanggan dan alokasikan ke invoice.", label: "Penerimaan" },
  supplier_payment: { title: "Pembayaran Pemasok", description: "Catat pembayaran ke pemasok dan alokasikan ke tagihan.", label: "Pembayaran" },
};

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="flex h-screen items-center justify-center text-slate-500" data-testid="loading-auth">
        Memuat data...
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<PasswordResetConfirmPage />} />
      <Route path="/" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/dasbor" replace />} />
        <Route path="dasbor" element={<DashboardPage />} />
        <Route path="executive-dashboard" element={<ExecutiveDashboard />} />
        <Route path="executive-alert-settings" element={<ExecutiveAlertSettings />} />
        <Route path="executive-targets" element={<ExecutiveTargets />} />
        <Route path="executive-reports" element={<ExecutiveReports />} />
        <Route path="executive-audit" element={<ExecutiveAuditView />} />
        <Route path="executive-alert-analytics" element={<ExecutiveAlertAnalytics />} />
        <Route path="executive-digest" element={<ExecutiveDigest />} />
        <Route path="executive-forecast-history" element={<ExecutiveForecastHistory />} />
        <Route path="executive-threshold-changes" element={<ExecutiveThresholdChanges />} />
        <Route path="executive-scenarios" element={<ExecutiveScenarios />} />
        <Route path="executive-scenarios/:id" element={<ExecutiveScenarioDetail />} />
        <Route path="executive-scenario-comparison" element={<ExecutiveScenarioComparison />} />
        <Route path="manajemen-pengguna" element={<UsersPage />} />
        <Route path="jejak-audit" element={<AuditPage />} />
        <Route path="data-master" element={<MasterDataLayout />}>
          <Route index element={<MasterDataHub />} />
          <Route path="format-nomor" element={<NumberingPage />} />
          {MASTER_LIST.map((m) => (
            <Route key={m.key} path={m.path} element={<MasterPage masterKey={m.key} />} />
          ))}
        </Route>
        <Route path="pengaturan" element={<SettingsPage />} />
        <Route path="pengaturan-demo" element={<DemoSettingsPage />} />
        <Route path="penjualan" element={<SalesHub />} />
        <Route path="penjualan/pesanan" element={<TransactionListPage masterKey="sales_order" config={TXN_CFG.sales_order} />} />
        <Route path="penjualan/invoice" element={<TransactionListPage masterKey="sales_invoice" config={TXN_CFG.sales_invoice} />} />
        <Route path="penjualan/nota-kredit" element={<TransactionListPage masterKey="sales_credit_note" config={TXN_CFG.sales_credit_note} />} />
        <Route path="penjualan/uang-muka" element={<TransactionListPage masterKey="customer_advance" config={TXN_CFG.customer_advance} showActions={false} />} />
        <Route path="pembelian" element={<PurchaseHub />} />
        <Route path="pembelian/pesanan" element={<TransactionListPage masterKey="purchase_order" config={TXN_CFG.purchase_order} />} />
        <Route path="pembelian/tagihan" element={<TransactionListPage masterKey="supplier_bill" config={TXN_CFG.supplier_bill} />} />
        <Route path="pembelian/nota-debit" element={<TransactionListPage masterKey="purchase_debit_note" config={TXN_CFG.purchase_debit_note} />} />
        <Route path="pembelian/uang-muka" element={<TransactionListPage masterKey="supplier_advance" config={TXN_CFG.supplier_advance} showActions={false} />} />
        <Route path="pembelian/beban" element={<ExpensePage />} />
        <Route path="piutang" element={<AgingPage kind="ar" />} />
        <Route path="hutang" element={<AgingPage kind="ap" />} />
        <Route path="keuangan" element={<FinanceHub />} />
        <Route path="keuangan/pusat-pembayaran" element={<PaymentCenterPage />} />
        <Route path="kas-bank" element={<FinanceHub />} />
        <Route path="kas-bank/penerimaan" element={<PaymentPage masterKey="customer_receipt" config={TXN_CFG.customer_receipt} />} />
        <Route path="kas-bank/pembayaran" element={<PaymentPage masterKey="supplier_payment" config={TXN_CFG.supplier_payment} />} />
        <Route path="kas-bank/transfer" element={<TransferPage />} />
        <Route path="persetujuan" element={<ApprovalInboxPage />} />
        <Route path="dokumen" element={<DocumentsPage />} />
        <Route path="akuntansi" element={<AccountingHub />} />
        <Route path="akuntansi/jurnal" element={<JournalListPage />} />
        <Route path="akuntansi/buku-besar" element={<GeneralLedgerPage />} />
        <Route path="akuntansi/neraca-saldo" element={<TrialBalancePage />} />
        <Route path="akuntansi/integritas" element={<IntegrityPage />} />
        <Route path="akuntansi/migrasi-saldo-awal" element={<OpeningBalancePage />} />
        <Route path="akuntansi/aset" element={<AssetsPage />} />
        <Route path="perpajakan" element={<TaxDashboard />} />
        <Route path="perpajakan/ppn" element={<TaxPPNHub />} />
        <Route path="perpajakan/faktur" element={<TaxFakturPage />} />
        <Route path="perpajakan/pph" element={<PPhHub />} />
        <Route path="perpajakan/pembayaran" element={<TaxPaymentPage />} />
        <Route path="perpajakan/kalender" element={<TaxCalendarPage />} />
        <Route path="perpajakan/kepatuhan" element={<CompliancePage />} />
        <Route path="perpajakan/rekonsiliasi" element={<PPhReconciliationPage />} />
        <Route path="perpajakan/integritas" element={<TaxIntegrityPage />} />
        <Route path="perpajakan/profil" element={<TaxProfilePage />} />
        <Route path="perpajakan/spt" element={<SPTPage />} />
        <Route path="perpajakan/arsip" element={<ArsipPage />} />
        <Route path="perpajakan/coretax-center" element={<CoretaxCenterPage />} />
        <Route path="perpajakan/integritas-final" element={<FinalIntegrityPage />} />
        <Route path="perpajakan/coretax-io" element={<CoretaxIOPage />} />
        <Route path="perpajakan/dasbor-eksekutif" element={<ExecutiveDashboardPage />} />
        <Route path="persediaan" element={<InventoryHub />} />
        <Route path="persediaan/saldo" element={<StockBalancePage />} />
        <Route path="persediaan/kartu-stok" element={<StockCardPage />} />
        <Route path="persediaan/mutasi" element={<MovementPage />} />
        <Route path="persediaan/transfer" element={<InvTransferPage />} />
        <Route path="persediaan/adjustment" element={<AdjustmentPage />} />
        <Route path="persediaan/opname" element={<OpnamePage />} />
        <Route path="persediaan/batch" element={<BatchPage />} />
        <Route path="persediaan/saldo-awal" element={<InvOpeningBalancePage />} />
        <Route path="persediaan/pengaturan-hpp" element={<CostingSettingsPage />} />
        <Route path="persediaan/integritas" element={<InvIntegrityPage />} />
        <Route path="persediaan/integritas-6b" element={<InvIntegrity6BPage />} />
        <Route path="persediaan/transfer-order" element={<InvTransferOrderPage />} />
        <Route path="persediaan/6c" element={<Inv6CDash />} />
        <Route path="persediaan/6c/expiry" element={<Inv6CExpiry />} />
        <Route path="persediaan/6c/reorder" element={<Inv6CReorder />} />
        <Route path="persediaan/6c/aging" element={<Inv6CAging />} />
        <Route path="persediaan/6c/margin" element={<Inv6CMargin />} />
        <Route path="persediaan/6c/notifikasi" element={<Inv6CNotif />} />
        <Route path="persediaan/6c/integritas" element={<Inv6CIntegrity />} />
        <Route path="bank" element={<BankHub />} />
        <Route path="bank/rekening" element={<BankAccountsPage />} />
        <Route path="bank/import" element={<StatementImportPage />} />
        <Route path="bank/matching" element={<MatchingPage />} />
        <Route path="bank/transfer" element={<BankTransferPage />} />
        <Route path="bank/biaya" element={<BankChargesPage />} />
        <Route path="bank/rekonsiliasi" element={<ReconciliationPage />} />
        <Route path="bank/integritas" element={<BankIntegrityPage />} />
        <Route path="bank/auto-rules" element={<BankAutoRulesPage />} />
        <Route path="fin" element={<FinancialHub />} />
        <Route path="fin/cashflow" element={<CashFlowPage />} />
        <Route path="fin/aging-piutang" element={<ARAgingPage />} />
        <Route path="fin/aging-hutang" element={<APAgingPage />} />
        <Route path="fin/cash-position" element={<CashPositionPage />} />
        <Route path="fin/dashboard" element={<ManagementDashboardPage />} />
        <Route path="fin/integritas" element={<FinancialIntegrityPage />} />
        <Route path="fin/tutup-buku" element={<FinancialClosePage />} />
        <Route path="aset/register" element={<FixedAssetRegisterPage />} />
        <Route path="aset/penyusutan" element={<DepreciationBatchPage />} />
        <Route path="aset/pelepasan" element={<AssetDisposalPage />} />
        <Route path="ekspor" element={<ExportsHub />} />
        <Route path="ekspor/audit" element={<ExportAuditLogPage />} />
        <Route path="ekspor/perusahaan" element={<CompanyProfilePage />} />
        <Route path="tahap7d" element={<BudgetHub />} />
        <Route path="tahap7d/budget/list" element={<BudgetListPage />} />
        <Route path="tahap7d/budget/new" element={<BudgetEditPage />} />
        <Route path="tahap7d/budget/:id" element={<BudgetEditPage />} />
        <Route path="tahap7d/budget/:id/variance" element={<BudgetVariancePage />} />
        <Route path="tahap7d/budget/dashboard" element={<BudgetDashboardPage />} />
        <Route path="tahap7d/forecast" element={<ForecastPage />} />
        <Route path="tahap7d/depreciation-methods" element={<DepreciationMethodsPage />} />
        <Route path="tahap7d/exports" element={<Exports7DHub />} />
        <Route path="hr" element={<HRHub />} />
        <Route path="hr/karyawan" element={<EmployeeListPage />} />
        <Route path="hr/karyawan/baru" element={<EmployeeEditPage />} />
        <Route path="hr/karyawan/:id" element={<EmployeeEditPage />} />
        <Route path="hr/policy" element={<HRPolicyListPage />} />
        <Route path="hr/absensi-hub" element={<AttendanceHub />} />
        <Route path="hr/kalender" element={<CalendarPage />} />
        <Route path="hr/absensi" element={<AttendanceClockPage />} />
        <Route path="hr/cuti" element={<LeavePage />} />
        <Route path="hr/lembur" element={<OvertimePage />} />
        <Route path="hr/koreksi" element={<CorrectionListPage />} />
        <Route path="hr/integritas-8b" element={<Integrity8BPage />} />
        {/* Tahap 8C + 8D — Payroll */}
        <Route path="payroll" element={<PayrollHub />} />
        <Route path="payroll/dashboard" element={<PayrollDashboard />} />
        <Route path="payroll/komponen" element={<PayrollComponents />} />
        <Route path="payroll/struktur-gaji" element={<SalaryStructurePage />} />
        <Route path="payroll/periode" element={<PayrollPeriodsPage />} />
        <Route path="payroll/periode/:pid" element={<PayrollPeriodDetail />} />
        <Route path="payroll/pinjaman" element={<LoanPage />} />
        <Route path="payroll/payment-batch" element={<PaymentBatchPage />} />
        <Route path="payroll/slip-gaji" element={<MyPayslipPage />} />
        <Route path="payroll/integritas" element={<PayrollIntegrityPage />} />
        {/* Tahap 8E — Claim & Reimbursement */}
        <Route path="klaim" element={<ClaimHub />} />
        <Route path="klaim/list" element={<ClaimListPage />} />
        <Route path="klaim/approval" element={<ClaimApprovalPage />} />
        <Route path="klaim/integritas" element={<ClaimIntegrityPage />} />
        {/* Tahap 8F — Recruitment / ATS */}
        <Route path="recruitment" element={<RecruitmentHub />} />
        <Route path="recruitment/mpr" element={<MPRPage />} />
        <Route path="recruitment/vacancies" element={<VacancyPage />} />
        <Route path="recruitment/candidates" element={<CandidatePage />} />
        <Route path="recruitment/offers" element={<OfferPage />} />
        <Route path="recruitment/integritas" element={<RecruitmentIntegrityPage />} />
        {/* Tahap 8G — Onboarding & Offboarding */}
        <Route path="onboarding" element={<OnboardingHub />} />
        <Route path="onboarding/proses" element={<OnboardingListPage />} />
        <Route path="onboarding/proses/:oid" element={<OnboardingDetailPage />} />
        <Route path="onboarding/template" element={<OnboardingTemplatePage />} />
        <Route path="onboarding/probation" element={<ProbationPage />} />
        <Route path="onboarding/integritas" element={<OnboardingIntegrityPage />} />
        <Route path="offboarding" element={<OffboardingHub />} />
        <Route path="offboarding/proses" element={<OffboardingListPage />} />
        <Route path="offboarding/proses/:oid" element={<OffboardingDetailPage />} />
        <Route path="offboarding/aset" element={<AssetAssignmentsPage />} />
        <Route path="offboarding/integritas" element={<OffboardingIntegrityPage />} />
        {/* Tahap 8H — Dashboard/ESS/MSS/Approval/Notif/Docs/Reports */}
        <Route path="hrd-dashboard" element={<HRDDashboardPage />} />
        <Route path="admin-dashboard" element={<AdminDashboardPage />} />
        <Route path="ess" element={<ESSHomePage />} />
        <Route path="ess/kehadiran" element={<ESSAttendancePage />} />
        <Route path="ess/cuti" element={<ESSLeavePage />} />
        <Route path="ess/lembur" element={<ESSOvertimePage />} />
        <Route path="ess/klaim" element={<ESSClaimPage />} />
        <Route path="ess/payslip" element={<ESSPayslipPage />} />
        <Route path="ess/pinjaman" element={<ESSLoanPage />} />
        <Route path="ess/dokumen" element={<ESSDocumentsPage />} />
        <Route path="ess/onboarding" element={<ESSOnboardingTasksPage />} />
        <Route path="mss" element={<MSSPage />} />
        <Route path="approval-center" element={<ApprovalCenterPage />} />
        <Route path="notifikasi" element={<NotificationCenterPage />} />
        <Route path="password-reset-admin" element={<PasswordResetAdminPage />} />
        <Route path="dokumen-hr" element={<DocumentCenterPage />} />
        <Route path="laporan-hr" element={<ReportCenterPage />} />
        <Route path="laporan" element={<ReportsHub />} />
        <Route path="profil" element={<ProfilePage />} />
        <Route path="akses-ditolak" element={<ForbiddenPage />} />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </div>
  );
}
